//functions
import { connect } from 'react-redux';
import * as ComponentFunctions from './Settlement.Form.Functions';
import { useEffect, useState } from 'react';
import { Input, InputOnChangeData, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideHelpText } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideLinkButton } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import SettlementDetailsByConceptComponent from '../../Settlements/SettlementDetails/SettlementDetailsByConcept.Component';
import { buildProviderSettlement, conflictSettlementIdOf, SettlementCreateError } from '../../../functions/SettlementKind.Functions';
import { formatCurrencyAmount } from '../../../commons/currency/Currency.Functions';

////i18Next
import { useTranslation } from 'react-i18next';

//styles
import './Settlement.Form.Styles.css';
import { NewAgreementSettlement, NewPractitionerSettlement, NewProtocolSettlement } from '../../../models/Entities/Settlements/NewSettlement';


interface FormValidation {
    success: boolean;
    errors: string[];
}


const SettlementFormComponent = (props: ComponentFunctions.Props) => {

    /** Modo proveedor (spec 010): alta tipo 4 con nombre opcional y período informativo. */
    const providerMode = !!props.Provider;

    // form status
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>(undefined);

    // form properties
    let defaultSettlementName = '';
    if (props.MedicalInsurance) {
        defaultSettlementName = props.MedicalInsurance.businessName;
    } else if (props.Practitioner) {
        defaultSettlementName = props.Practitioner.fullName;
    } else if (props.Protocol) {
        defaultSettlementName = props.Protocol.title;
    }
    const [settlementName, setSettlementName] = useState<string | undefined>(providerMode ? '' : ComponentFunctions.SuggestSettlementTitle(defaultSettlementName));
    const [settlementDescription, setSettlementDescription] = useState<string>();
    const [settlementStartDate, setSettlementStartDate] = useState<Date>();
    const [settlementEndDate, setSettlementEndDate] = useState<Date>();

    // modo proveedor: período informativo (mes actual), envío y error del alta (409 con la liquidación en curso)
    const [period, setPeriod] = useState(() => ComponentFunctions.currentMonthPeriod());
    const [providerSubmitting, setProviderSubmitting] = useState(false);
    const [providerError, setProviderError] = useState<SettlementCreateError | undefined>(undefined);

    // Remove commented code: //useTranslation();
    const [t] = useTranslation();

    //efects
    useEffect(() => {
        if (settlementName === undefined) {
            if (providerMode) {
                // el nombre es opcional: vacío = nombre por defecto de la API
                setSettlementName('');
                return;
            }
            let entity = '';
            if (props.MedicalInsurance) {
                entity = props.MedicalInsurance.businessName;
            } else if (props.Practitioner) {
                entity = props.Practitioner.fullName;
            } else if (props.Protocol) {
                entity = props.Protocol.title;
            }
            setSettlementName(ComponentFunctions.SuggestSettlementTitle(entity));
        }
    }, [settlementName]);

    useEffect(() => {
        if (props.ContractId) {
            props.GetContractById(props.ContractId);
        }
    }, [props.Contract?.id === undefined]);

    useEffect(() => {
        if (props.isSuccessfullyAdded && !providerMode) {
            ResetForm();
            props.dismissPanel();
        }
    }, [props.isSuccessfullyAdded]);

    // After successful practitioner settlement creation, show SettlementDetailsByConcept
    useEffect(() => {
        if (props.isSuccessfullyAdded && props.PractitionerId) {
            // Show the new details component for practitioner settlements
            // You may want to use a modal, panel, or route navigation here
            // For demonstration, render below the form
        }
    }, [props.isSuccessfullyAdded, props.PractitionerId]);

    // event handlers
    const onChangeSettlementName = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined) => {

        setSettlementName(newValue);
    }

    const onChangeSettlementDescription = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined) => {

        setSettlementDescription(newValue);
    }

    /** Alta de proveedor: cuerpo tipo 4 (sin name / description vacíos; currencyCode sólo con una moneda). */
    const SaveProviderSettlement = async () => {
        if (!props.Provider || providerSubmitting) return;
        setProviderSubmitting(true);
        setProviderError(undefined);
        try {
            const settlement = await props.AddProviderSettlement(buildProviderSettlement({
                provider: props.Provider,
                name: settlementName,
                description: settlementDescription,
                from: ComponentFunctions.dateInputToUtc(period.from),
                to: ComponentFunctions.dateInputToUtc(period.to),
                currencies: (props.ToSettleTotals?.byCurrency ?? []).map((currency) => currency.currencyCode),
            }));
            ResetForm();
            props.dismissPanel();
            props.onCreated?.(settlement);
        } catch (error: unknown) {
            setProviderError(error as SettlementCreateError);
        } finally {
            setProviderSubmitting(false);
        }
    };

    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            if (providerMode) {
                SaveProviderSettlement();
                return;
            }

            // Prepare safe values for required fields
            const safeName = settlementName ?? '';
            const safeDescription = settlementDescription ?? '';
            const safeStartDate = settlementStartDate ?? new Date();
            const safeEndDate = settlementEndDate ?? new Date();

            if (props.Protocol ?? false) {
                const protocolSettlement: NewProtocolSettlement = {
                    name: safeName,
                    contractId: props.ContractId,
                    description: safeDescription,
                    settlementType: 0,
                    settlementDateRangeInit: safeStartDate,
                    settlementDateRangeEnd: safeEndDate,
                    protocolId: props.Protocol!.id,
                    isManualSettlement: false,
                    isIncludedAdditionalConcepts: true,
                    isIncludedRefundableConcepts: true,
                    isIncludeContractConcepts: true
                }
                props.AddProtocolSettlement(protocolSettlement);
            }

            if (props.Practitioner ?? false) {
                const practitionerSettlement: NewPractitionerSettlement = {
                    name: safeName,
                    contractId: props.ContractId,
                    description: safeDescription,
                    settlementType: 0,
                    settlementDateRangeInit: safeStartDate,
                    settlementDateRangeEnd: safeEndDate,
                    practitionerId: props.Practitioner!.userId,
                    isManualSettlement: false
                }
                props.AddPractitionerSettlement(practitionerSettlement);
            }

            if (props.MedicalInsuranceId ?? false) {
                const medicalInsuranceSettlement: NewAgreementSettlement = {
                    name: safeName,
                    description: safeDescription,
                    settlementType: 0,
                    settlementDateRangeInit: safeStartDate,
                    settlementDateRangeEnd: safeEndDate,
                    medicalAgreementId: 0,
                    medicalInsuranceId: 0,
                    isManualSettlement: false
                }
                props.AddAgreementSettlement(medicalInsuranceSettlement);
            }
        }
    };

    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    // form validation
    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        if (providerMode) {
            // nombre opcional (DEC-APP-008); el período es informativo pero tiene que ser coherente
            if (!period.from || !period.to || period.from > period.to) {
                validationResult = false;
                FormValidation.errors.push(t('settlementForm.formValidation.errorDate'));
            }
        } else if ((settlementName === undefined || settlementName === '')) {
            validationResult = false;
            FormValidation.errors.push(t('settlementForm.formValidation.requiredName'));
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    const ResetForm = () => {
        setSubmittedForm(false);
        setSettlementName(undefined);
        setSettlementDescription(undefined);
        setSettlementStartDate(undefined);
        setSettlementEndDate(undefined);
        setPeriod(ComponentFunctions.currentMonthPeriod());
        setProviderError(undefined);
    }

    // form content

    // Contexto del encabezado: para quién es la liquidación.
    let context: string | undefined;
    if (props.Provider) {
        context = [props.Provider.businessName, props.Provider.taxId].filter(Boolean).join(' · ') || undefined;
    } else if ((props.PractitionerId || props.Contract?.practitioners[0]?.practitionerId) && props.Practitioner) {
        context = [props.Practitioner.fullName, props.Practitioner.speciality].filter(Boolean).join(' · ');
    } else if (props.MedicalInsuranceId || props.MedicalInsurance) {
        context = [props.MedicalInsurance?.businessName, props.MedicalAgreement?.healthProviderId].filter(Boolean).join(' · ') || undefined;
    } else if (props.Protocol) {
        context = props.Protocol.title;
    }

    const hasEntity = !!(props.Provider || props.Practitioner || props.Protocol || props.MedicalInsurance);
    const nameMissing = !providerMode && submittedForm && (!settlementName || settlementName === '');
    const periodInvalid = providerMode && submittedForm && (!period.from || !period.to || period.from > period.to);
    const conflictId = providerError && Number(providerError.ErrorCode) === 409 ? conflictSettlementIdOf(providerError) : undefined;

    const renderProviderError = () => {
        if (!providerError) return null;
        if (Number(providerError.ErrorCode) === 409) {
            return (
                <GuideMessageBar intent="warning" layout="multiline" data-testid="settlement-form-conflict">
                    <MessageBarBody>
                        <MessageBarTitle>{t('providerSettlement.conflict.title')}</MessageBarTitle>
                        {providerError.ErrorMessage}
                    </MessageBarBody>
                    {conflictId !== undefined && props.onOpenSettlement && (
                        <MessageBarActions>
                            <GuideLinkButton onClick={() => props.onOpenSettlement?.(conflictId)} data-testid="settlement-form-conflict-link">
                                {t('providerSettlement.conflict.link')}
                            </GuideLinkButton>
                        </MessageBarActions>
                    )}
                </GuideMessageBar>
            );
        }
        return (
            <GuideMessageBar intent="error" layout="multiline" data-testid="settlement-form-provider-error">
                <MessageBarBody>
                    <MessageBarTitle>{t('providerSettlement.error')}</MessageBarTitle>
                    {providerError.ErrorMessage}
                    {providerError.Errors && providerError.Errors.length > 0 && (
                        <ul>{providerError.Errors.map((item: string) => <li key={item}>{item}</li>)}</ul>
                    )}
                </MessageBarBody>
            </GuideMessageBar>
        );
    };

    const renderProviderSummary = () => {
        const totals = props.ToSettleTotals;
        if (!totals) return null;
        return (
            <div className="sf-provider-summary" data-testid="settlement-form-summary">
                <span className="sf-provider-summary__title">{t('providerSettlement.summary', { count: totals.totalCount })}</span>
                {totals.byCurrency.length > 0 && (
                    <ul className="sf-provider-summary__totals">
                        {totals.byCurrency.map((currency) => (
                            <li key={currency.currencyCode}>
                                <StatusPill tone="neutral">{currency.currencyCode}</StatusPill>
                                <span className="ichtys-num sf-provider-summary__amount">{formatCurrencyAmount(currency.amount, currency.currencyCode)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    return (
        <>
            <GuideDrawer
                open={props.isOpen}
                size="small"
                title={t('settlementForm.panel.newSettlement')}
                eyebrow={context}
                onDismiss={props.dismissPanel}
                testId="settlement-form-drawer"
                secondaryAction={{ label: t('settlementForm.footer.cancelButton'), onClick: HandleCancelForm }}
                primaryAction={hasEntity ? {
                    label: t('settlementForm.footer.saveButton'),
                    onClick: HandleSaveForm,
                    loading: props.isAdding || providerSubmitting,
                } : undefined}
            >
                {!providerMode && props.failOnAdding && (
                    <GuideMessageBar intent="error" layout="multiline">
                        <MessageBarBody>
                            <MessageBarTitle>Atención:</MessageBarTitle>
                            {props.error?.ErrorMessage}
                            {props.error?.Errors && props.error.Errors.length > 0 && (
                                <ul>{props.error.Errors.map((item: string) => <li key={item}>{item}</li>)}</ul>
                            )}
                        </MessageBarBody>
                    </GuideMessageBar>
                )}
                {providerMode && renderProviderError()}
                {submittedForm && !FormValidationStatus?.success && (
                    <GuideMessageBar intent="error" layout="multiline">
                        <MessageBarBody>
                            <MessageBarTitle>Verifique la información suministrada.</MessageBarTitle>
                            <ul>{FormValidationStatus?.errors.map((item: string) => <li key={item}>{item}</li>)}</ul>
                        </MessageBarBody>
                    </GuideMessageBar>
                )}
                {providerMode && renderProviderSummary()}
                {hasEntity && (
                    <GuideDrawerForm onSubmit={() => !props.isAdding && !providerSubmitting && HandleSaveForm()} testId="settlement-form">
                        <GuideField
                            label={t('settlementForm.formData.settlementName')}
                            required={!providerMode}
                            hint={providerMode && props.Provider
                                ? t('providerSettlement.nameHelp', { name: props.Provider.businessName, date: ComponentFunctions.todayText() })
                                : undefined}
                            error={nameMissing ? t('settlementForm.formData.requiredField') : undefined}
                        >
                            <Input
                                name="settlement-name"
                                type="text"
                                autoComplete="off"
                                onChange={(event, data: InputOnChangeData) => onChangeSettlementName(event, data.value)}
                                value={settlementName ?? ''}
                            />
                        </GuideField>
                        <GuideField label={t('settlementForm.formData.settlementDescription')}>
                            <Input
                                name="settlement-description"
                                type="text"
                                autoComplete="off"
                                onChange={(event, data: InputOnChangeData) => onChangeSettlementDescription(event, data.value)}
                                value={settlementDescription ?? ''}
                            />
                        </GuideField>
                        {providerMode && (
                            <div className="sf-period">
                                <GuideField
                                    label={t('settlementForm.formData.dateFrom')}
                                    error={periodInvalid ? t('settlementForm.formValidation.errorDate') : undefined}
                                >
                                    <Input
                                        name="settlement-period-from"
                                        type="date"
                                        value={period.from}
                                        onChange={(_event, data: InputOnChangeData) => setPeriod((current) => ({ ...current, from: data.value }))}
                                    />
                                </GuideField>
                                <GuideField label={t('settlementForm.formData.dateUtil')}>
                                    <Input
                                        name="settlement-period-to"
                                        type="date"
                                        value={period.to}
                                        onChange={(_event, data: InputOnChangeData) => setPeriod((current) => ({ ...current, to: data.value }))}
                                    />
                                </GuideField>
                                <GuideHelpText>{t('providerSettlement.periodHelp')}</GuideHelpText>
                            </div>
                        )}
                    </GuideDrawerForm>
                )}
                {!(!props.Contract || !props.MedicalInsuranceId) && hasEntity && (
                    <GuideHelpText>{t('settlementForm.medicalInsurance.noContractAssociations')}</GuideHelpText>
                )}
            </GuideDrawer>
            {/* Show details by concept if practitioner settlement was just created */}
            {props.isSuccessfullyAdded && props.PractitionerId && props.Contract?.id && (
                <SettlementDetailsByConceptComponent settlementId={props.Contract.id} getSettlementById={props.GetSettlementById} />
            )}
        </>
    )
}

export default connect(ComponentFunctions.mapStateToProps, ComponentFunctions.mapDispatchToProps)(SettlementFormComponent as any);
